"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const config_1 = require("config");
const Config_1 = require("../config/development/Config");
const UrlHelper_1 = require("../helpers/UrlHelper");
const Utils_1 = require("../helpers/Utils");
const Environment_1 = require("../models/Environment");
const JoinInfo_1 = require("../models/JoinInfo");
const TelemetryEvents_1 = require("../models/telemetry/TelemetryEvents");
const EnvironmentService_1 = require("./EnvironmentService");
const Logger_1 = require("./Logger");
const TelemetryScenarioTracker_1 = require("./TelemetryScenarioTracker");
const numbers = /^[0-9]+$/;
class BootstrapService {
    constructor(featureFlags) {
        this.featureFlags = featureFlags;
        this.getJoinInfo = () => {
            return {
                isValid: !!this.conversationUrl && !!this.type,
                deepLinkIds: this.deepLinkIds,
                correlationId: this.correlationId,
                conversationUrl: this.conversationUrl,
                deepLinkUrl: this.deepLinkUrl,
                type: this.type,
                isPrivate: this.isPrivate,
                threadId: this.threadId,
                messageId: this.messageId,
                meetingInfo: this.meetingInfo,
                shareEmailInfo: this.shareEmailInfo,
                guestAccessTenantId: this.guestAccessTenantId,
                ring: this.ring,
                isAnonymousEnabled: this.isAnonymousEnabled,
                forceWebJoin: this.forceWebJoin,
                code: this.code,
                launchAgent: this.launchAgent,
                laEntry: this.launchAgentEntry,
                laExpId: this.launchAgentExperimentId,
                isMeetingCreation: this.isMeetingCreation,
                isTflLink: this.isTflLink,
            };
        };
        this.getUserInfo = () => {
            const ring = (SERVER_PARAMS &&
                Object.prototype.hasOwnProperty.call(SERVER_PARAMS, "ring") &&
                SERVER_PARAMS.ring) ||
                undefined;
            const isPossiblyBot = this.isPossiblyBot();
            const userInfo = { ring, isPossiblyBot };
            if (this.featureFlags.enableUserInfoParsing) {
                try {
                    const token = window.localStorage.getItem("msal.idtoken") || window.localStorage.getItem("adal.idtoken");
                    if (token) {
                        const base64 = token.split(".")[1];
                        const userProfile = JSON.parse(window.atob(base64));
                        userInfo.oid = userProfile.oid;
                        userInfo.tid = userProfile.tid;
                    }
                }
                catch (error) {
                    const errorEvent = new TelemetryEvents_1.GenericErrorEvent({
                        message: error.message,
                        error: "Cannot parse user profile",
                    });
                    Logger_1.default.log(errorEvent);
                }
            }
            if (EnvironmentService_1.default.getEnvironmentInfo().deploymentType === Environment_1.DeploymentTypes.Life || userInfo.tid === Config_1.default.tflTenantId) {
                userInfo.tenantModel = 3;
            }
            return userInfo;
        };
        this.getDeepLinkIds = (queryParams, entityType) => {
            const clientId = Utils_1.Utils.generateGuid();
            const isCallingDeeplink = entityType === JoinInfo_1.JoinType.Meetup || entityType === JoinInfo_1.JoinType.Call || entityType === JoinInfo_1.JoinType.MeetupWithCode;
            return {
                server: this.getQueryParam("deeplinkId", queryParams),
                client: clientId,
                web: isCallingDeeplink ? Utils_1.Utils.generateGuid() : clientId,
            };
        };
        this.getQueryParam = (key, queryParams) => {
            const value = queryParams[key];
            return Array.isArray(value) ? value[0] : value;
        };
        this.getContextInfo = () => {
            if (this.type !== JoinInfo_1.JoinType.Meetup) {
                return;
            }
            const threadContext = this.getQueryParam("context", this.conversationQueryParams);
            try {
                return JSON.parse(threadContext);
            }
            catch (parseError) {
                try {
                    let sanitizedThreadContext = threadContext.replace(/(?:['"])?([\w-]+)(?:['"])?\s*:\s*(?:['"])?([\w-]+)(?:['"])?/g, (_, key, value) => {
                        return '"' + key + '":"' + value + '"';
                    });
                    const emailIncluded = sanitizedThreadContext.indexOf("@") > -1;
                    const telIncluded = sanitizedThreadContext.indexOf("tel:") > -1;
                    // trying to strip characters out of JSON literal object definition
                    const start = sanitizedThreadContext.indexOf("{");
                    const end = sanitizedThreadContext.indexOf("}");
                    sanitizedThreadContext = sanitizedThreadContext.substring(start > -1 ? start : 0, end > -1 ? end + 1 : undefined);
                    const context = JSON.parse(sanitizedThreadContext);
                    Logger_1.default.log(new TelemetryEvents_1.InitialParsingFailedEvent({
                        newContext: sanitizedThreadContext,
                        contextContainedEmail: emailIncluded,
                        contextContainedTel: telIncluded,
                    }));
                    // replace the malformed URL
                    this.conversationUrl = this.conversationUrl.replace(encodeURIComponent(threadContext), encodeURIComponent(sanitizedThreadContext));
                    return context;
                }
                catch (sanitizedParseError) {
                    Logger_1.default.log(new TelemetryEvents_1.ParsingFailedEvent());
                    return;
                }
            }
        };
        this.getShareEmailInfo = () => {
            if (this.type !== JoinInfo_1.JoinType.ShareEmail) {
                return;
            }
            // Get email id from QSP or from the last part of URL path
            const emailId = this.getQueryParam("emailId", this.conversationQueryParams) || UrlHelper_1.UrlHelper.pathParts(this.conversationUrl).slice(-1)[0];
            const attachmentCount = parseInt(this.getQueryParam("attachmentCount", this.conversationQueryParams), 10) || 0;
            const attachmentSize = parseInt(this.getQueryParam("attachmentSize", this.conversationQueryParams), 10) || 0;
            const fileTypes = this.getQueryParam("fileTypes", this.conversationQueryParams);
            const tid = this.getQueryParam("tid", this.conversationQueryParams);
            const uid = this.getQueryParam("uid", this.conversationQueryParams);
            let webUrl = `${UrlHelper_1.UrlHelper.getOrigin()}/share/email/create?emailRestId=${emailId}`;
            if (attachmentCount) {
                webUrl += `&attachmentCount=${attachmentCount}`;
            }
            if (attachmentSize) {
                webUrl += `&attachmentSize=${attachmentSize}`;
            }
            if (fileTypes) {
                webUrl += `&fileTypes=${fileTypes}`;
            }
            if (tid) {
                webUrl += `&outlookTid=${tid}`;
            }
            if (uid) {
                webUrl += `&outlookUid=${uid}`;
            }
            return {
                webUrl,
            };
        };
        this.getMeetingInfo = () => {
            if (!this.context) {
                return;
            }
            const data = this.context;
            if (!data || !data.Tid || !data.Oid) {
                return;
            }
            return {
                tenantId: data.Tid,
                organizerId: data.Oid,
                isBroadcast: data.IsBroadcastMeeting === true ||
                    data.b === 1 ||
                    data.b === "1",
                isComingFromWebClient: data.Web === "true",
            };
        };
        this.getThreadId = (splitDeepLinkUrl) => {
            if (splitDeepLinkUrl.length >= 4) {
                return Utils_1.Utils.scrubEuii(splitDeepLinkUrl[3]);
            }
            return;
        };
        this.getCode = (splitDeepLinkUrl) => {
            if (splitDeepLinkUrl.length > 2 && splitDeepLinkUrl[2].match(numbers)) {
                return Utils_1.Utils.scrubEuii(splitDeepLinkUrl[2]);
            }
            return;
        };
        this.getMessageId = (splitDeepLinkUrl) => {
            if (splitDeepLinkUrl.length > 4 && splitDeepLinkUrl[4].match(numbers)) {
                return Utils_1.Utils.scrubEuii(splitDeepLinkUrl[4]);
            }
            return;
        };
        this.getIsPrivate = (messageId) => {
            return messageId === "0";
        };
        this.recordJoinAttempt = () => {
            const joinAttemptStorageKey = "lastMeetingContext";
            const maxTimeForRepeatedMeetingLaunch = 3600000;
            const currentLaunchTime = new Date().getTime();
            let retryCount = 0;
            const previousDeeplinkIdList = [];
            try {
                const storageItem = localStorage.getItem(joinAttemptStorageKey);
                if (storageItem) {
                    const lastJoinAttempt = JSON.parse(storageItem);
                    const lastLaunchTime = lastJoinAttempt.timestamp;
                    if (lastJoinAttempt.meetingId === this.threadId &&
                        currentLaunchTime - lastLaunchTime <
                            maxTimeForRepeatedMeetingLaunch) {
                        retryCount = lastJoinAttempt.retryCount + 1;
                        previousDeeplinkIdList.push(...lastJoinAttempt.previousDeeplinkIdList);
                    }
                }
                previousDeeplinkIdList.push(this.deepLinkIds.client);
                const joinAttempt = {
                    meetingId: this.threadId,
                    retryCount,
                    timestamp: currentLaunchTime,
                    previousDeeplinkIdList,
                };
                localStorage.setItem(joinAttemptStorageKey, JSON.stringify(joinAttempt));
                return joinAttempt;
            }
            catch (error) {
                // Navigator can have localStorage disabled (or private mode)
                return {
                    meetingId: this.threadId,
                    retryCount: 0,
                    timestamp: currentLaunchTime,
                    previousDeeplinkIdList: new Array(this.deepLinkIds.client),
                };
            }
        };
        const startScenario = TelemetryScenarioTracker_1.TelemetryScenarioTracker.getOrCreateScenario(TelemetryScenarioTracker_1.ScenarioNames.JoinLauncherStart);
        this.queryParams = UrlHelper_1.UrlHelper.getQueryParameters();
        this.conversationUrl = this.getQueryParam("url", this.queryParams);
        const typeValue = this.getQueryParam("type", this.queryParams);
        this.deepLinkIds = this.getDeepLinkIds(this.queryParams, typeValue);
        this.userInfo = this.getUserInfo();
        Logger_1.default.log(new TelemetryEvents_1.LoadingLauncherStartEvent());
        if (!this.conversationUrl || !typeValue) {
            Logger_1.default.setContext(this.deepLinkIds, typeValue, this.userInfo);
            Logger_1.default.log(new TelemetryEvents_1.LoadingLauncherErrorEvent());
            startScenario.fail("Missing required parameters");
            UrlHelper_1.UrlHelper.redirect(config_1.default.urls.paths.error());
            return;
        }
        this.type = typeValue;
        this.ring = this.getQueryParam("ring", this.queryParams);
        this.conversationQueryParams = UrlHelper_1.UrlHelper.getQueryParameters(this.conversationUrl);
        this.forceWebJoin =
            this.getQueryParam("webjoin", this.conversationQueryParams) ===
                "true";
        this.context = this.getContextInfo();
        this.meetingInfo = this.getMeetingInfo();
        this.shareEmailInfo = this.getShareEmailInfo();
        this.extendLoggerContext();
        this.isAnonymousEnabled =
            this.getQueryParam("anon", this.conversationQueryParams) === "true";
        this.guestAccessTenantId = this.getQueryParam("tenantId", this.conversationQueryParams);
        this.deepLinkUrl = this.conversationUrl.replace("/_#", "");
        const deepLinkUrlParts = UrlHelper_1.UrlHelper.stripParams(this.deepLinkUrl).split("/");
        this.messageId = this.getMessageId(deepLinkUrlParts);
        this.isPrivate = this.getIsPrivate(this.messageId);
        this.threadId = this.getThreadId(deepLinkUrlParts);
        if (this.type === JoinInfo_1.JoinType.MeetupWithCode) {
            this.code = this.getCode(deepLinkUrlParts);
        }
        this.isTflLink = this.type === JoinInfo_1.JoinType.TflInvite ||
            this.type === JoinInfo_1.JoinType.TflClaim ||
            this.type === JoinInfo_1.JoinType.TflFunnel ||
            this.type === JoinInfo_1.JoinType.TflContactSync;
        this.launchAgent = this.getQueryParam("launchAgent", this.conversationQueryParams);
        this.launchAgentEntry = this.getQueryParam("laEntry", this.conversationQueryParams);
        // ?v=value for TFL links, laExpId for outlook
        this.launchAgentExperimentId = this.isTflLink
            ? this.getQueryParam("v", this.conversationQueryParams)
            : this.getQueryParam("laExpId", this.conversationQueryParams);
        this.isMeetingCreation = !!this.getQueryParam("isMeetingCreation", this.conversationQueryParams);
        this.correlationId = this.getQueryParam("correlationId", this.conversationQueryParams);
        Logger_1.default.log(new TelemetryEvents_1.ParsingCompleteEvent({
            isPrivate: this.isPrivate,
            threadId: this.threadId,
            type: this.type,
            code: this.code,
        }));
        this._logLoadingComplete();
        startScenario.markStep(TelemetryScenarioTracker_1.ScenarioStepNames.Bootstrap, TelemetryScenarioTracker_1.ScenarioStatuses.Success, {
            correlationId: this.correlationId,
            launchAgent: this.launchAgent,
            laEntry: this.launchAgentEntry,
            laExpId: this.launchAgentExperimentId,
            isMeetingCreation: this.isMeetingCreation,
        });
        window.addEventListener("load", () => {
            this._reportScriptErrorIfAny();
        });
    }
    _reportScriptErrorIfAny() {
        for (const scriptErrorLog of scriptErrorLogs) {
            if (scriptErrorLog) {
                const errorEvent = new TelemetryEvents_1.ScriptErrorEvent({
                    message: scriptErrorLog.message,
                    error: JSON.stringify({
                        source: Utils_1.Utils.stripQSPFromUrl(scriptErrorLog.source),
                        line: scriptErrorLog.line,
                        col: scriptErrorLog.col,
                        error: (!!scriptErrorLog.error && scriptErrorLog.error.stack),
                    }),
                });
                Logger_1.default.log(errorEvent);
            }
        }
    }
    isPossiblyBot() {
        try {
            const de = window.document.documentElement;
            const documentKeys = Object.keys(window.document);
            return !!Object.keys(window).find((key) => [
                "_phantom",
                "callPhantom",
                "__nightmare",
                "_selenium",
                "callSelenium",
                "_Selenium_IDE_Recorder",
                "Cypress"
            ].indexOf(key) > -1) // check of explicit values on window
                || !!documentKeys.find((key) => key.indexOf("__") === 0 && (key.indexOf("driver") > -1 || key.indexOf("selenium") > -1)) // check if property with driver or selenium in name on document object
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                || !!documentKeys.find((key) => key.match(/\$[a-z]dc_/) && Object.prototype.hasOwnProperty.call(window.document[key], "cache_"))
                || !!de.getAttribute("selenium") || !!de.getAttribute("driver") || !!de.getAttribute("webdriver");
        }
        catch (e) {
            return false;
        }
    }
    extendLoggerContext() {
        const launchSource = this.getQueryParam("lmsrc", this.conversationQueryParams);
        const context = this.type === JoinInfo_1.JoinType.Meetup && this.context ? JSON.stringify(this.context) : undefined;
        Logger_1.default.setContext(this.deepLinkIds, this.type, this.userInfo, launchSource, context);
    }
    _logLoadingComplete() {
        let retryCount;
        let previousDeeplinkIds;
        if (this.featureFlags.reportUserRepeatTelemetry &&
            this.type === JoinInfo_1.JoinType.Meetup) {
            const joinAttempt = this.recordJoinAttempt();
            retryCount = joinAttempt.retryCount;
            previousDeeplinkIds = joinAttempt.previousDeeplinkIdList.join(",");
        }
        Logger_1.default.log(new TelemetryEvents_1.LoadingLauncherCompleteEvent({
            retryCount,
            previousDeeplinkIdList: previousDeeplinkIds,
            organizerId: this.meetingInfo && this.meetingInfo.organizerId,
            tenantId: this.meetingInfo && this.meetingInfo.tenantId,
            threadId: this.threadId,
            code: this.code,
        }));
    }
}
exports.BootstrapService = BootstrapService;
